import type { TourDefinition, TourId } from "./types";

/**
 * Registry of every product onboarding tour shipped with AutopilotQA.
 *
 * Step `element` selectors target `data-tour="..."` attributes placed at the
 * relevant DOM site (sidebar items, dashboard cards, etc.). If the element is
 * not present on the active route, driver.js renders the step centered as a
 * modal — handy for welcome / wrap-up steps.
 */
export const TOURS: Record<TourId, TourDefinition> = {
  "first-login": {
    id: "first-login",
    steps: [
      {
        id: "welcome",
        title: "Welcome to AutopilotQA",
        description:
          "We'll take a quick 60-second tour to show you around. You can skip any time and restart it later from the Help menu.",
      },
      {
        id: "sidebar",
        title: "Your project navigation",
        description:
          "All your modules — Test Cases, Test Runs, Defects, Reports — live here. The sidebar collapses to give your work more room.",
        element: '[data-tour="sidebar-nav"]',
        side: "right",
        align: "start",
      },
      {
        id: "project-switcher",
        title: "Current Project",
        description:
          "Your current project shows here. You can switch projects, workspaces, and versions in the Settings menu.",
        element: '[data-tour="sidebar-header"]',
        side: "right",
        align: "start",
      },
      {
        id: "dashboard-overview",
        title: "Your test fleet at a glance",
        description:
          "This dashboard shows automation coverage, recent runs, and the health of every reserved suite. Use the period selector to widen or narrow the window.",
        element: '[data-tour="dashboard-hero"]',
        side: "bottom",
        align: "start",
      },
      {
        id: "next-scenarios",
        title: "Next: Build your first scenario",
        description:
          "Open <strong>Test Cases</strong> from the sidebar to add a folder and create your first test case.",
        element: '[data-tour="sidebar-test-cases"]',
        side: "right",
        align: "start",
      },
    ],
  },

  "setup-checklist": {
    id: "setup-checklist",
    steps: [
      {
        id: "welcome-back",
        title: "You're in — let's get set up",
        description:
          "Your account is ready. This short tour walks through the four things you need to do before you can run your first test. It takes about 2 minutes.",
      },
      {
        id: "create-project",
        title: "Step 1 — Create a project",
        description:
          "A <strong>Project</strong> is one application or domain you're testing (e.g. <em>app.myproduct.com</em>). Head to <strong>Settings → Projects</strong> and create your first one. Everything — test cases, runs, results — lives inside a project.",
        element: '[data-tour="sidebar-settings"]',
        side: "right",
        align: "start",
      },
      {
        id: "invite-team",
        title: "Step 2 — Invite your team",
        description:
          "Go to <strong>Settings → Users</strong> to invite teammates by email. Assign each person a role (Test Lead, Tester, Developer, or Project Manager) to control what they can see and do.",
        element: '[data-tour="sidebar-settings"]',
        side: "right",
        align: "start",
      },
      {
        id: "setup-environment",
        title: "Step 3 — Set up an environment",
        description:
          "Create at least one <strong>Environment</strong> in <strong>Settings → Environments</strong>. Add a <code>baseUrl</code> key pointing to your app (e.g. <em>https://staging.myapp.com</em>). You'll reference it in test steps using <code>*|baseUrl|</code> and select it when running a test plan.",
        element: '[data-tour="sidebar-settings"]',
        side: "right",
        align: "start",
      },
      {
        id: "create-test",
        title: "Step 4 — Write your first test case",
        description:
          "Open <strong>Test Cases</strong> from the sidebar, create a folder, then hit <strong>Create Test Case</strong>. Add steps, assign selectors using <strong>Elements</strong>, and you're ready to run.",
        element: '[data-tour="sidebar-test-cases"]',
        side: "right",
        align: "start",
      },
      {
        id: "run-it",
        title: "You're ready to run",
        description:
          "Once your test cases are written, head to <strong>Test Runs</strong> to trigger a run — pick your environment, browser, and hit go. Results, logs, and screenshots appear here automatically.",
        element: '[data-tour="sidebar-nav"]',
        side: "right",
        align: "start",
      },
    ],
  },

  "first-scenario": {
    id: "first-scenario",
    steps: [
      {
        id: "folders-vs-cases",
        title: "Folders vs. test cases",
        description:
          "Folders organise related test cases (think: feature areas, modules, releases). A test case is a single scenario you can run.",
      },
      {
        id: "create-button",
        title: "Create your first test case",
        description:
          'Click <strong>"Create Test Case"</strong> to add a manual or automation case to the active folder.',
        element: '[data-tour="scenarios-create-button"]',
        side: "bottom",
        align: "end",
      },
      {
        id: "modes",
        title: "Automation vs. Manual",
        description:
          "Automation cases run via Playwright in the cloud or on your machines. Manual cases capture step-by-step instructions for a human tester.",
        element: '[data-tour="scenarios-create-button"]',
        side: "bottom",
        align: "end",
      },
    ],
  },

  "first-element": {
    id: "first-element",
    steps: [
      {
        id: "what-are-elements",
        title: "Reusable selectors",
        description:
          "An <strong>Element</strong> is a stable selector for a UI component (button, input, link). Reuse them across many test cases — when the locator changes, you fix it in one place.",
      },
      {
        id: "install-recorder",
        title: "Install the Recorder extension",
        description:
          "The Chrome Recorder extension captures elements as you click around your app. Install it once, then use it from any browser tab.",
        element: '[data-tour="elements-record-button"]',
        side: "bottom",
        align: "end",
      },
      {
        id: "record-or-add",
        title: "Record or add manually",
        description:
          'Hit <strong>"Record"</strong> to capture as you click, or <strong>"Create Elements"</strong> to enter selectors by hand.',
        element: '[data-tour="elements-create-button"]',
        side: "bottom",
        align: "end",
      },
      {
        id: "locator-types",
        title: "Pick the most stable locator",
        description:
          "Prefer <strong>ID</strong> (most stable) → then unique <strong>CSS</strong> → fall back to <strong>XPath</strong> only when you have to. Stable locators = fewer flaky tests.",
      },
    ],
  },

  "first-plan": {
    id: "first-plan",
    steps: [
      {
        id: "what-is-a-plan",
        title: "What's a Scheduled Run?",
        description:
          "A <strong>Scheduled Run</strong> groups one or more test suites with a schedule and an end condition (e.g. stop on first failure). It's the unit you point at CI or run nightly.",
      },
      {
        id: "add-suites",
        title: "Add suites",
        description:
          "Pick which test suites should run as part of this plan. You can mix automation and manual suites in one plan.",
        element: '[data-tour="plan-schedule-button"]',
        side: "bottom",
        align: "end",
      },
      {
        id: "pick-machines",
        title: "Pick test machines",
        description:
          "Choose the browsers, devices, and environments to run against. Add as many combinations as your plan needs.",
        element: '[data-tour="plan-schedule-button"]',
        side: "bottom",
        align: "end",
      },
      {
        id: "schedule",
        title: "Set schedule + end condition",
        description:
          "Run on demand, on a cron schedule, or via API trigger. Choose to stop on first failure, or run everything to completion.",
        element: '[data-tour="plan-schedule-button"]',
        side: "bottom",
        align: "end",
      },
    ],
  },

  "first-failure": {
    id: "first-failure",
    steps: [
      {
        id: "what-failed-means",
        title: 'What "Failed" means',
        description:
          "A failed test means at least one step did not match what you expected. The result page below breaks down which step failed and why.",
        element: '[data-tour="result-status-banner"]',
        side: "bottom",
        align: "start",
      },
      {
        id: "open-logs",
        title: "Logs, screenshots & traces",
        description:
          "Click any failed test case in the list to open its logs, screenshots, and (for automation runs) the full Playwright trace.",
        element: '[data-tour="result-list"]',
        side: "left",
        align: "start",
      },
      {
        id: "rerun-options",
        title: "Re-run options",
        description:
          "Use <strong>Re-Run</strong> to retry: a single failed case, the full plan, or just the cases that failed last time.",
        element: '[data-tour="result-rerun-button"]',
        side: "left",
        align: "center",
      },
      {
        id: "file-defect",
        title: "File a defect from this run",
        description:
          "Spot a real bug? File a defect right from a failed test case — the run, screenshot, and stack trace are linked automatically.",
        element: '[data-tour="result-list"]',
        side: "left",
        align: "start",
      },
    ],
  },
  // ── Guide Me: Create your first project ─────────────────────
  "create-project": {
    id: "create-project",
    steps: [
      {
        id: "welcome",
        title: "Create your first project",
        description:
          "A <strong>Project</strong> is one application or product you're testing. Everything — test cases, cycles, reports — lives inside a project. Let's walk through setting one up.",
      },
      {
        id: "new-project",
        title: "Click the + button",
        description:
          "Hit the <strong>+</strong> button at the top of the left panel to open the New Project form.",
        element: '[data-tour="projects-new-btn"]',
        side: "right",
        align: "start",
      },
      {
        id: "fill-name",
        title: "Name your project",
        description:
          "Give it a clear name like 'Mobile App' or 'Customer Portal'. Add a website URL so the team knows which product is under test.",
      },
      {
        id: "assign-team",
        title: "Assign a Lead and Manager",
        description:
          "Set a <strong>Test Lead</strong> (runs the day-to-day testing) and a <strong>Manager</strong> (owns quality sign-off). These show up in reports and release readiness scores.",
      },
      {
        id: "create",
        title: "Save the project",
        description:
          "Click <strong>Create</strong>. The project is now active — you can invite the team, add workspaces, and start creating test cases right away.",
      },
      {
        id: "next-steps",
        title: "What's next?",
        description:
          "Head to <strong>Guide Me → Invite Members</strong> to bring in your team, or go to <strong>Test Cases</strong> to start writing your first scenario.",
        element: '[data-tour="sidebar-test-cases"]',
        side: "right",
        align: "start",
      },
    ],
  },

  // ── Guide Me: Invite members & roles ────────────────────────
  "invite-members": {
    id: "invite-members",
    steps: [
      {
        id: "open-settings",
        title: "Open Settings",
        description:
          "Click <strong>Settings</strong> in the sidebar to access user and role management.",
        element: '[data-tour="sidebar-settings"]',
        side: "right",
        align: "start",
      },
      {
        id: "go-users",
        title: "Go to Users",
        description:
          "Select the <strong>Users</strong> tab to see everyone currently in your workspace.",
      },
      {
        id: "invite",
        title: "Invite by email",
        description:
          "Click <strong>Invite Members</strong>, enter the email address, and pick a role:<br><br>• <strong>Owner</strong> — full access<br>• <strong>Manager</strong> — manage releases and reports<br>• <strong>Test Lead</strong> — run cycles and assign testers<br>• <strong>Tester</strong> — execute assigned cases<br>• <strong>Developer</strong> — view and fix defects",
      },
      {
        id: "roles",
        title: "Roles control permissions",
        description:
          "Each role unlocks specific features. Testers can only see their assigned cases; Managers see everything. You can change a role at any time from the Users page.",
      },
      {
        id: "done",
        title: "Team is ready",
        description:
          "Invited teammates receive an email link to join. Once they accept, they appear in your project and can be assigned to cycles.",
      },
    ],
  },

  // ── Guide Me: Tour your dashboard ───────────────────────────
  "tour-dashboard": {
    id: "tour-dashboard",
    steps: [
      {
        id: "open",
        title: "Open the Dashboard",
        description:
          "Click <strong>Dashboard</strong> in the sidebar to open the Automation Control Center.",
        element: '[data-tour="sidebar-dashboard"]',
        side: "right",
        align: "start",
      },
      {
        id: "hero",
        title: "Summary cards",
        description:
          "The top row shows your automation coverage, manual pass rate, active defects, and test velocity at a glance.",
        element: '[data-tour="dashboard-hero"]',
        side: "bottom",
        align: "start",
      },
      {
        id: "workflows",
        title: "Active workflows",
        description:
          "The middle section shows which test runs are currently live and their real-time status. A red row means a run is failing right now.",
      },
      {
        id: "insights",
        title: "AI insights panel",
        description:
          "The right panel surfaces AI-generated quality signals — flaky tests, velocity drops, and coverage gaps — so you can act before they become blockers.",
      },
      {
        id: "period",
        title: "Change the time window",
        description:
          "Use the period selector (top right) to view trends over the last 7, 30, or 90 days.",
      },
      {
        id: "done",
        title: "Dashboard mastered",
        description:
          "Bookmark this page as your morning check-in. All the context you need for a standup is here in one screen.",
      },
    ],
  },

  // ── Guide Me: Reusable UI elements ──────────────────────────
  "reusable-elements": {
    id: "reusable-elements",
    steps: [
      {
        id: "open",
        title: "Open UI Elements",
        description:
          "Click <strong>UI Elements</strong> in the sidebar. This is where you store reusable selectors for your app's components.",
        element: '[data-tour="sidebar-ui-elements"]',
        side: "right",
        align: "start",
      },
      {
        id: "what",
        title: "What is a UI Element?",
        description:
          "An <strong>Element</strong> is a stable selector (ID, CSS, XPath) for a button, input, or link. Store it once — reference it in many test cases. When the locator changes, you fix it in one place.",
      },
      {
        id: "create",
        title: "Create or record an element",
        description:
          "Click <strong>Create Element</strong> to add a selector manually, or install the Chrome Recorder to capture elements as you click around your app.",
        element: '[data-tour="elements-create-button"]',
        side: "bottom",
        align: "end",
      },
      {
        id: "locator-tips",
        title: "Pick the most stable locator",
        description:
          "Prefer <strong>ID</strong> (most stable) → unique <strong>CSS</strong> → fall back to <strong>XPath</strong> only as a last resort. Stable locators mean fewer flaky tests.",
      },
      {
        id: "done",
        title: "Ready to reuse",
        description:
          "Once saved, elements appear in the test step editor as suggestions. Select them instead of typing raw selectors.",
      },
    ],
  },

  // ── Guide Me: Test data profiles ────────────────────────────
  "test-data-profiles": {
    id: "test-data-profiles",
    steps: [
      {
        id: "open",
        title: "Open Test Data",
        description:
          "Click <strong>Test Data</strong> in the sidebar to manage data profiles.",
        element: '[data-tour="sidebar-test-data"]',
        side: "right",
        align: "start",
      },
      {
        id: "what",
        title: "What are data profiles?",
        description:
          "A <strong>Profile</strong> is a named set of fields (e.g. username, password, account type). You reference it in a test case and the runner swaps in real values at execution time — no hard-coded test data in your steps.",
      },
      {
        id: "create",
        title: "Create a profile",
        description:
          "Click <strong>New Profile</strong>, give it a name, then add fields. Each field can have multiple values to drive data-driven testing.",
      },
      {
        id: "use",
        title: "Use it in a test step",
        description:
          "In the test step editor, reference a profile field with <code>*|FieldName|*</code>. The runner replaces it with the correct value at run time.",
      },
    ],
  },

  // ── Guide Me: Requirement coverage ──────────────────────────
  "requirements-coverage": {
    id: "requirements-coverage",
    steps: [
      {
        id: "open",
        title: "Open Requirements",
        description:
          "Click <strong>Requirements</strong> in the sidebar to manage your tracked requirements.",
        element: '[data-tour="sidebar-requirements"]',
        side: "right",
        align: "start",
      },
      {
        id: "create",
        title: "Add a requirement",
        description:
          "Click <strong>New Requirement</strong> and describe the feature or acceptance criterion you need to verify.",
      },
      {
        id: "link",
        title: "Link test cases",
        description:
          "Open a test case and use the <strong>Requirements</strong> field to map it. One requirement can be covered by many test cases.",
      },
      {
        id: "report",
        title: "Check coverage in Reports",
        description:
          "Go to <strong>Reports → Coverage</strong> to see which requirements have zero, partial, or full test coverage — and which are failing.",
      },
    ],
  },

  // ── Guide Me: Defect workflow ────────────────────────────────
  "defect-workflow": {
    id: "defect-workflow",
    steps: [
      {
        id: "open",
        title: "Open Defects",
        description:
          "Click <strong>Defects</strong> in the sidebar to see all raised issues.",
        element: '[data-tour="sidebar-defects"]',
        side: "right",
        align: "start",
      },
      {
        id: "raise",
        title: "Raising a defect",
        description:
          "Defects are usually raised inline during execution (from the test runner). You can also create one manually here by clicking <strong>New Defect</strong>.",
      },
      {
        id: "triage",
        title: "Triage: severity and owner",
        description:
          "Set a <strong>severity</strong> (Blocker → Minor) and assign it to a developer. Blockers automatically surface in the release readiness score.",
      },
      {
        id: "retest",
        title: "Mark as Fixed → Retest",
        description:
          "When a developer marks the defect <strong>Fixed</strong>, it lands in the Fix Queue for retesting. Testers verify the fix and either close it or reopen it.",
      },
      {
        id: "done",
        title: "Closed loop",
        description:
          "Once a defect is closed, the linked test case's pass rate updates automatically in all cycle and release reports.",
      },
    ],
  },

  // ── Guide Me: Release readiness ─────────────────────────────
  "release-readiness": {
    id: "release-readiness",
    steps: [
      {
        id: "open",
        title: "Open Reports",
        description:
          "Click <strong>Reports</strong> in the sidebar and select the <strong>Release Readiness</strong> tab.",
        element: '[data-tour="sidebar-reports"]',
        side: "right",
        align: "start",
      },
      {
        id: "score",
        title: "The readiness score",
        description:
          "The score (0–100) is calculated from pass rate, open blockers, coverage, and velocity. A score below 70 is a risk signal — investigate before shipping.",
      },
      {
        id: "blockers",
        title: "Blocker breakdown",
        description:
          "The blockers panel lists every open defect that is suppressing the score. Each row links to the defect so you can assign it directly.",
      },
      {
        id: "forecast",
        title: "Completion forecast",
        description:
          "Based on current velocity, AutopilotQA projects when the cycle will complete. Use this to communicate realistic dates to stakeholders.",
      },
      {
        id: "share",
        title: "Share with stakeholders",
        description:
          "Click <strong>Share</strong> to generate a read-only link. Stakeholders see live data without needing an account.",
        element: '[data-tour="reports-share-btn"]',
        side: "bottom",
        align: "end",
      },
    ],
  },

  // ── Guide Me: Import & export ────────────────────────────────
  "import-export": {
    id: "import-export",
    steps: [
      {
        id: "open",
        title: "Open Settings",
        description:
          "Go to <strong>Settings</strong> in the sidebar, then find the <strong>Import / Export</strong> section.",
        element: '[data-tour="sidebar-settings"]',
        side: "right",
        align: "start",
      },
      {
        id: "import",
        title: "Import test cases",
        description:
          "Upload a <strong>CSV or Excel</strong> file to bulk-import test cases. Download the template first to see the required column format.",
      },
      {
        id: "export",
        title: "Export your data",
        description:
          "Export all test cases, results, or defects to CSV or Excel for offline analysis, auditing, or migrating to another project.",
      },
      {
        id: "tips",
        title: "Tips for a clean import",
        description:
          "Keep titles unique and use the exact column headers from the template. Empty required fields will cause that row to be skipped — check the error summary after import.",
      },
      {
        id: "done",
        title: "Data is portable",
        description:
          "Everything in AutopilotQA can be exported. Your test assets are never locked in.",
      },
    ],
  },

  // ── Guide Me: Manage files & attachments ────────────────────
  "manage-files": {
    id: "manage-files",
    steps: [
      {
        id: "open",
        title: "Open Files",
        description:
          "Click <strong>Files</strong> in the sidebar to access your uploaded assets.",
        element: '[data-tour="sidebar-files"]',
        side: "right",
        align: "start",
      },
      {
        id: "upload",
        title: "Upload a file",
        description:
          "Click <strong>Upload</strong> and select any file (image, PDF, CSV, video). Files are stored at project level and can be referenced from any test step.",
      },
      {
        id: "reference",
        title: "Reference in a test step",
        description:
          "In the test step editor, use the attachment picker to link a file as supporting evidence or expected output.",
      },
      {
        id: "evidence",
        title: "Attach evidence during execution",
        description:
          "During manual execution, use the <strong>Attach</strong> button on any step to upload a screenshot or screen recording inline.",
      },
    ],
  },

  // ── Guide Me: Browser & device matrix ───────────────────────
  "browser-matrix": {
    id: "browser-matrix",
    steps: [
      {
        id: "intro",
        title: "Browser & device matrix",
        description:
          "AutopilotQA can run your automation tests in parallel across multiple browsers and device sizes — catching cross-browser bugs before release.",
      },
      {
        id: "configure",
        title: "Configure in Settings",
        description:
          "Go to <strong>Settings → Environments</strong> to add browser/device combinations. Each combination becomes an execution lane in your automation runs.",
        element: '[data-tour="sidebar-settings"]',
        side: "right",
        align: "start",
      },
      {
        id: "select",
        title: "Select matrix at run time",
        description:
          "When creating or starting a cycle, use the <strong>Browser Matrix</strong> picker to choose which combinations to run. More combinations = more coverage but longer run time.",
      },
      {
        id: "results",
        title: "View results by browser",
        description:
          "In the execution results view, filter by browser/device to isolate failures that only happen on Safari or mobile viewport.",
      },
      {
        id: "tips",
        title: "Parallelism tip",
        description:
          "AutopilotQA fans out lanes automatically. A 10-case suite on 3 browsers runs all 30 combinations concurrently — same wall-clock time as a single-browser run.",
      },
    ],
  },

  // ── Guide Me: AI Site Sync ───────────────────────────────────
  "ai-site-sync": {
    id: "ai-site-sync",
    steps: [
      {
        id: "intro",
        title: "AI Site Sync",
        description:
          "Point AutopilotQA at your live app and let AI crawl it to discover UI flows, interactive elements, and testable screens — automatically.",
      },
      {
        id: "open",
        title: "Open UI Elements",
        description:
          "Go to <strong>UI Elements</strong> in the sidebar and click <strong>Site Sync</strong> to start a crawl.",
        element: '[data-tour="sidebar-ui-elements"]',
        side: "right",
        align: "start",
      },
      {
        id: "configure",
        title: "Configure the crawl",
        description:
          "Enter your app's base URL, set a crawl depth, and optionally provide login credentials so AI can access authenticated pages.",
      },
      {
        id: "review",
        title: "Review discovered elements",
        description:
          "After the crawl, AI presents discovered elements grouped by page. Review and approve the ones you want to add to your element library.",
      },
      {
        id: "generate",
        title: "Auto-generate test cases",
        description:
          "From discovered flows, AI can suggest test cases covering happy paths, edge cases, and form validations — ready to review and save.",
      },
    ],
  },

  // ── Guide Me: Self-healing automation ───────────────────────
  "self-healing": {
    id: "self-healing",
    steps: [
      {
        id: "intro",
        title: "Self-healing automation",
        description:
          "When a UI change breaks a locator, AutopilotQA's AI finds the new selector automatically — no manual script maintenance required.",
      },
      {
        id: "how",
        title: "How it works",
        description:
          "During a failing run, AI tries alternative locators (nearby text, ARIA role, nearby ID) to find the element. If it succeeds, the healed locator is flagged for your review.",
      },
      {
        id: "review",
        title: "Review healed locators",
        description:
          "Go to <strong>UI Elements → Healing Log</strong> to see every suggested fix. Approve or reject each one — approved fixes update the element library permanently.",
      },
      {
        id: "confidence",
        title: "Confidence scoring",
        description:
          "Each healed locator has a confidence score (0–100%). High-confidence fixes can be set to auto-approve; low-confidence ones always require human review.",
      },
    ],
  },

  // ── Guide Me: Generate tests with AI ─────────────────────────
  "ai-generate-tests": {
    id: "ai-generate-tests",
    steps: [
      {
        id: "open-test-cases",
        title: "Open the Test Cases workspace",
        description:
          "Click <strong>Test Cases</strong> in the sidebar. This is where all your suites and cases live.",
        element: '[data-tour="sidebar-test-cases"]',
        side: "right",
        align: "start",
      },
      {
        id: "click-ai-generate",
        title: "Click Generate with AI",
        description:
          "Find the <strong>Generate with AI</strong> button in the toolbar (the sparkle icon) and click it to open the AI prompt panel.",
        element: '[data-tour="ai-generate-btn"]',
        side: "bottom",
        align: "end",
      },
      {
        id: "describe-feature",
        title: "Describe your feature",
        description:
          "Type a plain-English description of the feature or user journey you want to test.<br><br><strong>Tip:</strong> Be specific — 'Login with invalid credentials shows an error message' produces far better cases than just 'Login'.",
      },
      {
        id: "review-cases",
        title: "Review the generated test cases",
        description:
          "AutopilotQA will suggest a list of test cases. Read each one and deselect any that don't apply.<br><br><strong>Best practice:</strong> Keep cases atomic — one scenario per test case makes failures easier to diagnose.",
      },
      {
        id: "save-to-suite",
        title: "Save to suite",
        description:
          "Click <strong>Save</strong> to add the approved cases to your suite. They're ready to assign to a cycle immediately.<br><br><strong>Tip:</strong> You can regenerate at any time to get fresh alternatives for the same prompt.",
      },
    ],
  },

  // ── Guide Me: Create a test case ────────────────────────────
  "create-test-case": {
    id: "create-test-case",
    steps: [
      {
        id: "navigate",
        title: "Navigate to Test Cases",
        description:
          "Click <strong>Test Cases</strong> in the sidebar to open the workspace where all manual and automated cases are managed.",
        element: '[data-tour="sidebar-test-cases"]',
        side: "right",
        align: "start",
      },
      {
        id: "new-case",
        title: "Create a new test case",
        description:
          "Click the <strong>+ New</strong> button to open the test case editor.",
        element: '[data-tour="scenarios-create-button"]',
        side: "bottom",
        align: "end",
      },
      {
        id: "fill-details",
        title: "Fill in title and details",
        description:
          "Give the test case a clear, action-oriented title. Then set its <strong>priority</strong>, <strong>module</strong>, and <strong>type</strong> so it's easy to filter and search.",
      },
      {
        id: "write-steps",
        title: "Write your test steps",
        description:
          "Add steps one by one. Each step has an <strong>Action</strong> and an <strong>Expected Result</strong> field.<br><br><strong>Tip:</strong> Keep each action to a single user interaction — click, fill, select. Avoid combining multiple actions.",
      },
      {
        id: "link-requirement",
        title: "Link to a requirement",
        description:
          "Use the Requirements field to map this test case to a tracked requirement for coverage traceability.<br><br><strong>Heads up:</strong> Unmapped test cases won't appear in requirement coverage reports.",
      },
      {
        id: "save",
        title: "Save and publish",
        description:
          "Click <strong>Save</strong>. The test case is now available to add to any cycle. You can edit steps any time — changes apply to future executions only.",
      },
    ],
  },

  // ── Guide Me: Execute from My Queue ─────────────────────────
  "execute-my-queue": {
    id: "execute-my-queue",
    steps: [
      {
        id: "open-queue",
        title: "Open My Queue",
        description:
          "Click <strong>My Queue</strong> under Cycles in the sidebar. Your assigned test cases appear here — no noise from other testers.",
        element: '[data-tour="cycles-my-queue"]',
        side: "right",
        align: "start",
      },
      {
        id: "pick-case",
        title: "Pick a test case to run",
        description:
          "Click any test case in the list to open the execution runner.<br><br><strong>Tip:</strong> Cases are sorted by priority — start at the top to tackle the most critical ones first.",
      },
      {
        id: "read-steps",
        title: "Read each step carefully",
        description:
          "The runner shows steps one by one. Read the <strong>Action</strong> and <strong>Expected Result</strong> before you execute.<br><br><strong>Best practice:</strong> Don't rush — a careful run catches real bugs that speed-running misses.",
      },
      {
        id: "mark-result",
        title: "Mark each step Pass or Fail",
        description:
          "Use the <strong>Pass / Fail / Blocked</strong> buttons after each step. A comment is required for Fail.",
      },
      {
        id: "log-defect",
        title: "Log a defect inline",
        description:
          "If a step fails, click <strong>'Log Defect'</strong> to open the defect form without leaving the runner.<br><br><strong>Tip:</strong> Attach a screenshot right here — it saves you from filing the defect manually later.",
      },
      {
        id: "complete",
        title: "Complete the test",
        description:
          "Once all steps are marked, click <strong>Complete</strong>. The overall result is calculated automatically.<br><br><strong>Best practice:</strong> If even one step is Blocked, mark the whole case Blocked — don't guess.",
      },
    ],
  },

  // ── Guide Me: Cycle reports ──────────────────────────────────
  "cycle-reports": {
    id: "cycle-reports",
    steps: [
      {
        id: "navigate",
        title: "Navigate to Reports",
        description:
          "Click <strong>Reports</strong> in the sidebar to open the report hub — the single source of truth for cycle health, coverage, and readiness.",
        element: '[data-tour="sidebar-reports"]',
        side: "right",
        align: "start",
      },
      {
        id: "select-cycle",
        title: "Select a cycle",
        description:
          "Use the cycle picker at the top to choose the cycle you want to report on.<br><br><strong>Tip:</strong> You can compare two cycles side by side using the Compare toggle.",
      },
      {
        id: "read-summary",
        title: "Read the pass/fail summary",
        description:
          "The summary cards show total cases, pass rate, blocked count, and open defects.<br><br><strong>Best practice:</strong> A pass rate below 70% at the end of a cycle is a strong signal to delay the release.",
      },
      {
        id: "identify-blockers",
        title: "Identify blockers",
        description:
          "Scroll to the Defects section to see open blockers and who owns each one.<br><br><strong>Heads up:</strong> Unresolved blockers automatically reduce the release readiness score.",
      },
      {
        id: "export-share",
        title: "Export or share the report",
        description:
          "Use the <strong>Export</strong> button to download a PDF or Excel, or click <strong>Share</strong> to generate a read-only link.<br><br><strong>Tip:</strong> Shared links always reflect the latest data — you don't need to resend them.",
        element: '[data-tour="reports-share-btn"]',
        side: "bottom",
        align: "end",
      },
    ],
  },

  // ── Guide Me: Create your first cycle ────────────────────────
  "create-cycle": {
    id: "create-cycle",
    steps: [
      {
        id: "open-cycles",
        title: "Open the Cycles workspace",
        description:
          "Click <strong>Cycles</strong> in the sidebar. This is where every planned run lives — cycles group test cases into an executable plan tied to a release.",
        element: '[data-tour="sidebar-cycles"]',
        side: "right",
        align: "start",
      },
      {
        id: "new-cycle",
        title: "Create a new cycle",
        description:
          "Click <strong>New Cycle</strong> to open the composer. Give it a descriptive name like 'Sprint 42 — Regression'.<br><br><strong>Best practice:</strong> Use release + sprint + intent so the cycle is discoverable months later.",
      },
      {
        id: "attach-release",
        title: "Attach a release",
        description:
          "Bind the cycle to an active release so readiness rolls up automatically.<br><br><strong>Heads up:</strong> A cycle without a release will not appear in release reports.",
      },
      {
        id: "assign-cases",
        title: "Assign test cases",
        description:
          "Pick test cases from the library. Filter by module, tag, or priority.<br><br><strong>Tip:</strong> Use saved views to reuse the same selection across cycles.",
      },
      {
        id: "assign-testers",
        title: "Assign testers",
        description:
          "Distribute test cases across your team. AutopilotQA balances workload for you.<br><br><strong>Best practice:</strong> Leave 15% headroom for retesting and unplanned defects.",
      },
      {
        id: "launch",
        title: "Launch the cycle",
        description:
          "Hit <strong>Start</strong>. You'll be taken to live execution with real-time progress.<br><br><strong>Tip:</strong> You can return to Guide Me any time to start a different tour.",
      },
    ],
  },

  // ── Execution: Fix Queue ────────────────────────────────────
  "fix-queue": {
    id: "fix-queue",
    steps: [
      {
        id: "open",
        title: "Open the Fix Queue",
        description:
          "Click <strong>Cycles</strong> in the sidebar, then open <strong>Fix Queue</strong>. Every defect assigned to you across all cycles lands here in one list.",
        element: '[data-tour="sidebar-cycles"]',
        side: "right",
        align: "start",
      },
      {
        id: "review",
        title: "Review your defects",
        description:
          "Defects are grouped by priority, highest first. Each row shows the title, the cycle it came from, and the step that failed. Open one to read the full reproduction steps.",
      },
      {
        id: "fixed",
        title: "Mark as Fixed",
        description:
          "Once your fix is merged, click <strong>Mark Fixed</strong>. The tester is notified to retest it on the next run.",
      },
      {
        id: "filter",
        title: "Filter by status",
        description:
          "Use the status filter to switch between Open, In Progress, Fixed, and All. Open is your daily to-do list.",
      },
    ],
  },

  // ── Execution: Schedules ────────────────────────────────────
  schedules: {
    id: "schedules",
    steps: [
      {
        id: "open",
        title: "Open an automated cycle",
        description:
          "Click <strong>Cycles</strong> in the sidebar and open an automated cycle. Schedules live inside each automated cycle.",
        element: '[data-tour="sidebar-cycles"]',
        side: "right",
        align: "start",
      },
      {
        id: "tab",
        title: "Go to the Schedules tab",
        description:
          "In the cycle workspace, open the <strong>Schedules</strong> tab. This is where you set up recurring runs.",
      },
      {
        id: "create",
        title: "Create a schedule",
        description:
          "Click <strong>New Schedule</strong>, choose a frequency and time, and save. The cycle runs on its own — results appear under Executions.",
      },
    ],
  },
};
